import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { HttpService } from '../http/http.service';
import { IApiClaimsHistory, IContractData } from '../../interfaces/IJob';
import { IMyPlanStory, MyPlanStoryScenario } from '../../interfaces/IMyPlanStory';
import { PLAN_STORY_URL } from '../../utils/api.util';
import { buildPlanStoryContext, buildPlanStoryStats } from '../../utils/plan-context.util';

// The backend only ever returns the generated narrative text — stats are
// computed deterministically in code (see buildPlanStoryStats) and never
// sent back, so a hallucinated number can never reach the UI.
interface IPlanStoryApiResponse {
  narrative: string;
}

interface IPlanStoryCacheEntry {
  key: string;
  story: IMyPlanStory;
}

@Injectable({
  providedIn: 'root',
})
export class MyPlanStoryService {
  // Last successfully generated story, keyed by its exact request (scenario +
  // payload). Every page hosting MyPlanStoryComponent (dashboard, My Plan, SR
  // history) builds the same payload for the same contract/jobs, so navigating
  // between them reuses this instead of re-calling the plan story API. Any change
  // to the jobs/contract changes the key, so a stale story is never served.
  private readonly storyCache$ = new BehaviorSubject<IPlanStoryCacheEntry | null>(null);
  // In-flight request for the same key, shared so two cards mounting before the
  // first response lands still trigger only one call.
  private inFlight: { key: string; request$: Observable<IMyPlanStory> } | null = null;

  constructor(private http: HttpService) {}

  /**
   * Posts the aggregate plan-story context (see buildPlanStoryContext) plus the
   * deterministically-computed stats to the backend, which generates the AI
   * narrative server-side. scenario is also sent as a query param so the
   * backend can pick the right prompt without re-deriving it from the body.
   *
   * contractNumber/clientId are included in this payload — unlike the earlier
   * direct-to-Anthropic POC, this call never leaves Cinch's own backend, so
   * sending account identifiers here is fine (needed for logging/entitlement)
   * rather than the third-party-PII risk that constrained the POC.
   *
   * The API responds with just `{ narrative }` — stats/generatedAt/source are
   * filled in here from the same context used to build the request.
   */
  getPlanStory(
    contractData: IContractData,
    jobsData: IApiClaimsHistory[],
    scenario: MyPlanStoryScenario = 'none',
    existingAlertMessage: string | null = null
  ): Observable<IMyPlanStory> {
    const context = buildPlanStoryContext(contractData, jobsData, scenario, existingAlertMessage);
    const stats = buildPlanStoryStats(context);
    const payload = { ...context, stats };
    const key = `${scenario}:${JSON.stringify(payload)}`;

    const cached = this.storyCache$.getValue();
    if (cached?.key === key) {
      return of(cached.story);
    }
    if (this.inFlight?.key === key) {
      return this.inFlight.request$;
    }

    const request$ = this.http
      .post<IPlanStoryApiResponse>(`${PLAN_STORY_URL}?scenario=${encodeURIComponent(scenario)}`, payload)
      .pipe(
        map(response => {
          // A 200 with no narrative is still a broken response — treat it the
          // same as a network/HTTP error so the caller falls back consistently
          // instead of rendering "undefined"/blank text as if it were real.
          if (!response?.narrative?.trim()){
            throw new Error('Plan story API returned no narrative');
          }
          return {
            narrative: response.narrative,
            stats,
            generatedAt: new Date().toISOString(),
            source: 'ai' as const,
            itemCount: context.claimsFiledThisYear,
          };
        }),
        // Only successes are cached — a failed call is retried on the next mount.
        tap(story => this.storyCache$.next({ key, story })),
        finalize(() => {
          if (this.inFlight?.key === key) {
            this.inFlight = null;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    this.inFlight = { key, request$ };
    return request$;
  }
}
